import {PulseUser} from 'atoms/auth';
import {Employee, Team} from 'client';
import {STEAM_TEAM_SLUG} from 'constant';

export const isNumeric = (str: string) => {
  return !isNaN(parseFloat(str)) && isFinite(Number(str));
};

export const isSuperUser = (user: PulseUser | null | undefined) => {
  if (!user) return false;
  return user.is_superuser;
};

export const isUserAdmin = (user: PulseUser | null | undefined) => {
  if (!user) return false;
  return user.is_useradmin;
};

export const isFinanceAdmin = (user: PulseUser | null | undefined) => {
  if (!user) return false;
  return user.is_finance_admin;
};

export const isSTeam = (employeeTeams?: Pick<Team, 'slug'>[] | null) =>
  (employeeTeams ?? []).some((team) => team.slug?.toLowerCase().trim() === STEAM_TEAM_SLUG);

export const canManagePlanningTemplates = (user: PulseUser | null | undefined) => {
  return isUserAdmin(user) || isSuperUser(user);
};

export const canViewEditorDocumentAdmin = (user: PulseUser | null | undefined) => {
  return isUserAdmin(user) || isSuperUser(user);
};

export const canViewLivePlanningAdmin = (user: PulseUser | null | undefined) => {
  return isSuperUser(user);
};

export const canManagePillarAdmin = (user: PulseUser | null | undefined) => {
  return isUserAdmin(user) || isSuperUser(user);
};

export const isContentEditor = (user: PulseUser | undefined) => {
  if (!user) return false;
  return user.is_contenteditor;
};

const COMPANY_EMAIL_DOMAINS = ['@dropbox.com', '@hellosign.com', '@dbx51.com'];

export const isCompanyEmail = (email: string | null | undefined): boolean =>
  Boolean(email && COMPANY_EMAIL_DOMAINS.some((domain) => email.endsWith(domain)));

// One way sha-256 hash of email address to be used as a unique identifier
export const hashEmail = async (email: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(email);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

// Sorts employees by level, tenure_percentile, and name
export const sortEmployees = (a: Employee, b: Employee) => {
  // Move employees with no level to the end
  if (!a.level && b.level) return 1;
  if (a.level && !b.level) return -1;
  if (!a.level && !b.level) return 0;

  // Extract prefix (e.g. "M" or "IC") and numeric part from the level
  const [prefixA = '', numA] = a.level?.match(/[A-Za-z]+|[\d]+/g) || [];
  const [prefixB = '', numB] = b.level?.match(/[A-Za-z]+|[\d]+/g) || [];

  // Parse numeric parts for comparison
  const levelANumber = parseInt(numA || '0', 10);
  const levelBNumber = parseInt(numB || '0', 10);

  // Sort by level number (higher is better)
  if (levelANumber !== levelBNumber) {
    return levelBNumber - levelANumber; // Higher levels come first
  }

  // If level numbers are the same, prioritize 'M' before 'IC'
  const prefixOrder = {M: 0, IC: 1};
  const orderA =
    prefixOrder[prefixA as keyof typeof prefixOrder] !== undefined
      ? prefixOrder[prefixA as keyof typeof prefixOrder]
      : 2;
  const orderB =
    prefixOrder[prefixB as keyof typeof prefixOrder] !== undefined
      ? prefixOrder[prefixB as keyof typeof prefixOrder]
      : 2;

  if (orderA !== orderB) {
    return orderA - orderB; // Managers (M) should go before ICs if level numbers are equal
  }

  // Sort by tenure (higher is better)
  const tenureA = a.tenure_percentile || 0;
  const tenureB = b.tenure_percentile || 0;

  if (tenureA !== tenureB) {
    return tenureB - tenureA; // Higher tenure first
  }

  // If hire dates are equal or either is missing, sort by name
  return a.name.localeCompare(b.name);
};

// Sorts team by name
export const sortTeams = (a: Team, b: Team) => {
  if (a.name && b.name) {
    return a.name.localeCompare(b.name);
  }
  return 0;
};

export const sortEmployeesSelfFirst = (employee: Employee) => (a: Employee, b: Employee) => {
  return a.ldap === employee.ldap ? -1 : b.ldap === employee.ldap ? 1 : sortEmployees(a, b);
};

export type Link = {
  url: string;
  icon: string;
  text: string;
  id: number;
  created_at: string;
  updated_at: string;
};

/*
 * enforceExhaustive makes sure at typecheck that this function will never be called.
 *
 *
 * Example usage:
 * function f(x: 'red' | 'blue') {
 *   switch (x) {
 *     case 'red': break;
 *     case 'blue': break;
 *     default:
 *       enforceExhaustive(x);
 *   }
 * }
 *
 * The way this works is that the call `enforceExhaustive(x)`
 * enforces that the type of `x` is castable to `never`.
 * `never` is basically the empty type, like an `|` of 0 types.
 * So if I forget a case, like this:
 *
 * function f(x: 'red' | 'blue') {
 *   switch (x) {
 *     case 'red': break;
 *     default:
 *       // `x` has type 'blue' here
 *       enforceExhaustive(x);
 *   }
 * }
 *
 * Then you can't implicitly cast `x` to `never` and you'll get a type error.
 *
 */
export const enforceExhaustive = (value: never): never => {
  return value;
};

// Creates a URL-friendly slug from a title string
export const createSlug = (title: string): string => {
  if (!title?.trim()) return '';

  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
};

// Extracts the ID from a slug-id URL segment (everything after the last hyphen)
export const extractIdFromSlug = (slug: string): string => {
  if (!slug) return '';

  // Handle legacy format (just the ID)
  if (!slug.includes('-')) return slug;

  // Extract ID from slug format (title-id)
  const lastHyphenIndex = slug.lastIndexOf('-');
  return lastHyphenIndex !== -1 ? slug.slice(lastHyphenIndex + 1) : slug;
};

// Extracts the planning ID from a full URL (e.g., /planning/switchboard-copy-34bmB9nmTa8)
export const extractPlanningIdFromUrl = (url: string): string => {
  if (!url) return '';

  try {
    // Extract pathname from URL (handles both full URLs and relative paths)
    let pathname = url;
    if (url.includes('://')) {
      pathname = new URL(url).pathname;
    }

    // Extract the last segment from the pathname
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    // Extract ID from the slug (everything after the last hyphen)
    return extractIdFromSlug(lastSegment);
  } catch {
    // If URL parsing fails, try to extract from the string directly
    const segments = url.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    return extractIdFromSlug(lastSegment);
  }
};
